import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import ContentLoader from '../components/ContentLoader';
import { useAuth } from '../auth/AuthContext';
import { taskService } from '../api/taskService';
import { eventService } from '../api/eventService';
import { userService } from '../api/userService';
import KanbanBoard from '../components/KanbanBoard';
import AnalyticsWidgets from '../components/AnalyticsWidgets';
import ActivityFeed from '../components/ActivityFeed';
import { Briefcase, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { getUserName } from '../utils/userUtils';

import RISLogo from '../assets/RIS-LoGO.png';
import AICLogo from '../assets/AIC_logo.png';
import FITMLogo from '../assets/FITM_LOGO.png';
import CMECLogo from '../assets/CMEC_LOGO.png';
import DakshinLogo from '../assets/DAKSHIN_logo.png';

const DivisionDashboard = ({ division }) => {
    const params = useParams();
    const activeDivision = division || params.divisionId;

    // Map CORE to RIS for display (Legacy check, now using RIS directly but keeping fallback just in case)
    const displayDivisionName = (activeDivision === 'CORE' || activeDivision === 'RIS') ? 'RIS' : (activeDivision || '').replace('_', ' ');



    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user) return;

        const groups = user.roles || [];
        const isDG = groups.includes('DG') || groups.includes('DG_OFFICE');
        const employeeGroup = groups.find(g => g.startsWith('EMPLOYEE_') || g.startsWith('COORDINATOR_'));

        // If not DG, enforce division check
        if (!isDG && employeeGroup) {
            const userDivision = employeeGroup.split('_')[1];
            if (activeDivision && userDivision && activeDivision !== userDivision) {
                console.warn(`Redirecting from ${activeDivision} to ${userDivision} due to role restrictions`);
                navigate(`/ division / ${userDivision} `);
            }
        }
    }, [user, activeDivision, navigate]);

    const { data: usersData = [], isLoading: loadingUsers } = useQuery({
        queryKey: ['users'],
        queryFn: () => userService.getAllUsers().catch(() => []),
        staleTime: 300000,
    });

    const { data: divisionData, isLoading: loadingDivision, error: divisionError } = useQuery({
        queryKey: ['division_data', activeDivision],
        queryFn: async () => {
            const [tasksData, eventsData, myEvents] = await Promise.all([
                taskService.getDivisionTasks(activeDivision),
                eventService.getDivisionEvents(activeDivision),
                eventService.getAll()
            ]);

            const allEvents = [...(eventsData || []), ...(myEvents || [])];
            const uniqueEvents = Array.from(new Map(allEvents.map(item => [item.EventID, item])).values());

            const filteredTasks = (tasksData || []).filter(t => (t.Status || t.status || '').toLowerCase() !== 'cancelled');
            const filteredEvents = uniqueEvents.filter(e => (e.Status || e.status || '').toLowerCase() !== 'cancelled');

            return {
                tasks: filteredTasks,
                events: filteredEvents,
                counts: {
                    divisionEvents: eventsData?.length || 0,
                    personalEvents: myEvents?.length || 0
                }
            };

        },
        staleTime: 60000,
        enabled: !!activeDivision
    });

    const tasks = divisionData?.tasks || [];
    const events = divisionData?.events || [];
    const counts = divisionData?.counts || { personalEvents: 0, divisionEvents: 0 };
    const users = Array.isArray(usersData) ? usersData : (usersData?.Users || []);

    const loading = loadingDivision || loadingUsers;
    const error = divisionError ? `Failed to load division data. ${divisionError.message}` : null;

    if (loading) return <ContentLoader text="Loading dashboard..." />;
    if (error) return <div style={{ padding: '1rem', color: 'red' }}>{error}</div>;

    const activeTasksCount = tasks.filter(t => t.Status !== 'Completed' && t.Status !== 'Cancelled').length;
    const upcomingEventsCount = events.filter(e => new Date(e.StartDateTime) > new Date()).length;
    const highPriorityCount = tasks.filter(t => t.Priority === 'High' && t.Status !== 'Completed').length;

    const StatCard = ({ title, value, total, icon, color }) => (
        <div style={{
            background: 'rgba(255, 255, 255, 0.8)', padding: '1rem', borderRadius: '12px',
            border: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem',
            boxShadow: '0 4px 16px rgba(15, 23, 42, 0.04)',
            position: 'relative', overflow: 'hidden', backdropFilter: 'blur(8px)',
            height: '100%', boxSizing: 'border-box'
        }}>
            <div style={{
                position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: color, opacity: 0.8
            }}></div>
            <div style={{
                background: `${color}1A`, color: color, padding: '0.6rem', borderRadius: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                {React.cloneElement(icon, { size: 20 })}
            </div>
            <div>
                <h5 style={{ margin: '0 0 0.15rem 0', color: '#64748b', fontSize: '0.75rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{title}</h5>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a' }}>{value}</span>
                    {total !== undefined && <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>/ {total}</span>}
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ padding: '1rem 2rem', maxWidth: '1600px', margin: '0 auto' }}>

            {/* Secondary Heading */}
            <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {(activeDivision === 'CORE' || activeDivision === 'RIS' || activeDivision === 'ADMIN' || activeDivision === 'PUBLICATION') && <img src={RISLogo} alt="RIS" style={{ height: '40px' }} />}
                {activeDivision === 'AIC' && <img src={AICLogo} alt="AIC" style={{ height: '40px' }} />}
                {activeDivision === 'FITM' && <img src={FITMLogo} alt="FITM" style={{ height: '40px' }} />}
                {activeDivision === 'CMEC' && <img src={CMECLogo} alt="CMEC" style={{ height: '40px' }} />}
                {activeDivision === 'DAKSHIN' && <img src={DakshinLogo} alt="Dakshin" style={{ height: '40px' }} />}

                <h2 style={{ fontSize: '1.5rem', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {displayDivisionName} <span style={{ fontWeight: 400, color: '#64748b' }}>Division</span>
                </h2>
            </div>

            {/* Top Overview Sections: Split Vertically */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>

                {/* Task Overview Matrix */}
                <div style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 55%, #ffffff 100%)',
                    padding: '1.5rem',
                    borderRadius: '18px',
                    border: '1px solid #bfdbfe',
                    boxShadow: '0 8px 24px rgba(37, 99, 235, 0.08)',
                    display: 'flex', flexDirection: 'column'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{
                            fontSize: '0.85rem',
                            color: '#ffffff',
                            background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
                            borderRadius: '999px',
                            padding: '0.45rem 1rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontWeight: 700,
                            margin: 0,
                            display: 'inline-block'
                        }}>
                            Task Overview
                        </h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', flex: 1 }}>
                        <StatCard
                            title="Active Tasks"
                            value={activeTasksCount}
                            total={tasks.length}
                            icon={<Briefcase />}
                            color="#3b82f6"
                        />
                        <StatCard
                            title="High Priority"
                            value={highPriorityCount}
                            icon={<AlertCircle />}
                            color="#ef4444"
                        />
                        <StatCard
                            title="Completion Rate"
                            value={`${tasks.length ? Math.round(((tasks.length - activeTasksCount) / tasks.length) * 100) : 0}%`}
                            icon={<CheckCircle />}
                            color="#10b981"
                        />
                        <StatCard
                            title="Total Tasks"
                            value={tasks.length}
                            icon={<Briefcase />}
                            color="#64748b"
                        />
                    </div>
                </div>

                {/* Event Overview Matrix */}
                <div style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 55%, #ffffff 100%)',
                    padding: '1.5rem',
                    borderRadius: '18px',
                    border: '1px solid #bfdbfe',
                    boxShadow: '0 8px 24px rgba(37, 99, 235, 0.08)',
                    display: 'flex', flexDirection: 'column'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{
                            fontSize: '0.85rem',
                            color: '#ffffff',
                            background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
                            borderRadius: '999px',
                            padding: '0.45rem 1rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontWeight: 700,
                            margin: 0,
                            display: 'inline-block'
                        }}>
                            Event Overview
                        </h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', flex: 1 }}>
                        <StatCard
                            title="Upcoming Events"
                            value={upcomingEventsCount}
                            total={events.length}
                            icon={<Calendar />}
                            color="#f59e0b"
                        />
                        <StatCard
                            title="Division Events"
                            value={counts.divisionEvents}
                            icon={<Calendar />}
                            color="#3b82f6"
                        />
                        <StatCard
                            title="Personal Events"
                            value={counts.personalEvents}
                            icon={<Calendar />}
                            color="#8b5cf6"
                        />
                        <StatCard
                            title="Total Events"
                            value={events.length}
                            icon={<Calendar />}
                            color="#64748b"
                        />
                    </div>
                </div>
            </div>

            {/* Middle Section: Analytics (Left) & Feed (Right) */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem', marginBottom: '2rem', alignItems: 'start' }}>
                <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 55%, #ffffff 100%)', padding: '1.5rem', borderRadius: '18px', border: '1px solid #bfdbfe', boxShadow: '0 8px 24px rgba(37, 99, 235, 0.08)' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.1rem', color: '#444' }}>Performance Analytics</h3>
                    <AnalyticsWidgets tasks={tasks} getUserName={(id) => getUserName(id, users)} users={users} activeDivision={activeDivision} compact={true} />
                </div>

                <div style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 55%, #ffffff 100%)', padding: '0', borderRadius: '18px', border: '1px solid #e2e8f0', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 24px rgba(100, 116, 139, 0.08)' }}>
                    <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', background: 'transparent' }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#444' }}>Live Activity</h3>
                    </div>
                    <div style={{ flex: 1, minHeight: '500px', maxHeight: '500px', overflowY: 'auto' }}>
                        <ActivityFeed tasks={tasks} events={events} getUserName={(id) => getUserName(id, users)} />
                    </div>
                </div>
            </div>

            {/* Bottom Section: Kanban Board */}
            <div>
                <h3 style={{ fontSize: '1.25rem', color: '#202124', marginBottom: '1.5rem' }}>Task Management Board</h3>
                <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 55%, #ffffff 100%)', padding: '1.5rem', borderRadius: '18px', border: '1px solid #bfdbfe', boxShadow: '0 8px 24px rgba(37, 99, 235, 0.08)' }}>
                    <KanbanBoard tasks={tasks} getUserName={(id) => getUserName(id, users)} />
                </div>
            </div>
        </div>
    );
};

export default DivisionDashboard;
